/**
 * radiologyQualityChecker.js
 * Clinical Report Quality Auditor & Assisted Impression Generator.
 * Evaluates report completeness, missing sections, unfilled placeholders,
 * critical finding consistency, and measurement annotations.
 */

const CRITICAL_KEYWORDS = [
  'hemorrhage', 'bleed', 'hematoma', 'infarction', 'stroke', 'ischemia',
  'pneumothorax', 'perforation', 'aortic dissection', 'fracture', 'rupture',
  'appendicitis', 'volvulus', 'intussusception', 'pulmonary embolism'
];

const PLACEHOLDER_PATTERNS = [
  /\[\s*\]/g,
  /_{3,}/g,
  /\bTBD\b/gi,
  /\bTODO\b/gi,
  /\[enter\s+[^\]]+\]/gi,
  /\[insert\s+[^\]]+\]/gi
];

class RadiologyQualityChecker {
  /**
   * Run comprehensive clinical quality check on report data.
   */
  validateReport(reportData = {}) {
    const issues = [];
    const passedChecks = [];
    let score = 100;

    const findings = (reportData.findings || '').replace(/<[^>]*>/g, ' ').trim();
    const impression = (reportData.impression || '').replace(/<[^>]*>/g, ' ').trim();
    const technique = (reportData.examinationTechnique || '').trim();
    const history = (reportData.clinicalHistory || '').trim();

    // 1. Mandatory Findings Check
    if (!findings || findings.length < 15) {
      issues.push({
        severity: 'error',
        title: 'Missing or Incomplete Findings',
        message: 'Detailed anatomical radiological findings are required.',
        suggestion: 'Document organ/region-specific findings or apply standard normal protocol.'
      });
      score -= 40;
    } else {
      passedChecks.push('Detailed anatomical findings documented.');
    }

    // 2. Mandatory Impression Check
    if (!impression || impression.length < 5) {
      issues.push({
        severity: 'error',
        title: 'Missing Diagnostic Impression',
        message: 'A definitive diagnostic clinical conclusion or impression is required before sign-off.',
        suggestion: 'Provide a concise primary diagnostic impression or accept the suggested impression.'
      });
      score -= 30;
    } else {
      passedChecks.push('Diagnostic clinical impression provided.');
    }

    // 3. Examination Technique Check
    if (!technique) {
      issues.push({
        severity: 'info',
        title: 'Technique Not Specified',
        message: 'Examination technique description is empty.',
        suggestion: 'Add standard projection/sequence details (e.g. PA view, contrast-enhanced).'
      });
      score -= 5;
    } else {
      passedChecks.push('Examination technique documented.');
    }

    // 4. Clinical History Check
    if (!history) {
      issues.push({
        severity: 'info',
        title: 'Clinical Indications Omitted',
        message: 'Referring clinical history or indication is blank.',
        suggestion: 'Record relevant patient symptoms or indications for clinical correlation.'
      });
      score -= 5;
    } else {
      passedChecks.push('Clinical indication/history provided.');
    }

    // 5. Unfilled Template Placeholders
    const allText = `${findings} ${impression}`;
    let foundPlaceholders = false;
    for (const pattern of PLACEHOLDER_PATTERNS) {
      if (pattern.test(allText)) {
        foundPlaceholders = true;
        break;
      }
    }
    if (foundPlaceholders) {
      issues.push({
        severity: 'warning',
        title: 'Unfilled Template Placeholders',
        message: 'Detected unfilled template markers (e.g., [ ], ___, TBD) in report content.',
        suggestion: 'Fill in specific clinical values or remove unfilled placeholder tokens.'
      });
      score -= 15;
    } else {
      passedChecks.push('No unfilled template placeholders detected.');
    }

    // 6. Critical Finding Consistency Check
    const lowerText = allText.toLowerCase();
    const detectedCriticalWords = CRITICAL_KEYWORDS.filter(word => lowerText.includes(word));
    if (detectedCriticalWords.length > 0 && !reportData.isCritical) {
      issues.push({
        severity: 'warning',
        title: 'Potential Unflagged Critical Finding',
        message: `Report mentions acute clinical term(s) (${detectedCriticalWords.join(', ')}), but "Critical Finding" is not flagged.`,
        suggestion: 'Check "Flag as Critical Finding" if this requires urgent clinical escalation.'
      });
      score -= 10;
    } else if (reportData.isCritical) {
      passedChecks.push('Critical finding workflow actively flagged.');
    }

    // 7. Measurement Consistency Check
    const measurementRegex = /(\d+(\.\d+)?)\s*(mm|cm)\b/gi;
    const hasMeasurements = measurementRegex.test(findings);
    if (hasMeasurements) {
      passedChecks.push('Objective anatomical measurements detected.');
    }

    // Score boundary
    score = Math.max(0, Math.min(100, score));

    let status = 'EXCELLENT';
    if (score < 50) status = 'CRITICAL_FAIL';
    else if (score < 75) status = 'WARNING';
    else if (score < 90) status = 'GOOD';

    const canFinalize = !issues.some(i => i.severity === 'error');

    return {
      score,
      status,
      issues,
      passedChecks,
      canFinalize
    };
  }

  /**
   * Assisted Clinical Intelligence:
   * Generates a suggested diagnostic impression from unstructured findings.
   * STRICTLY ASSISTIVE: Doctor must review and approve.
   */
  generateSuggestedImpression(findingsHtml = '') {
    if (!findingsHtml) return '';
    const cleanText = findingsHtml
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/p>/gi, '\n')
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;/g, ' ')
      .trim();

    if (cleanText.length < 20) return '';

    // Check for standard normal
    const lower = cleanText.toLowerCase();
    if (
      lower.includes('no focal abnormality') ||
      lower.includes('within normal limits') ||
      lower.includes('normal in size, shape') ||
      lower.includes('normal gray-white matter') ||
      (lower.includes('lungs are clear') && lower.includes('cardiothoracic ratio is normal'))
    ) {
      if (lower.includes('chest') || lower.includes('lung')) {
        return 'Normal chest radiograph. No acute cardiopulmonary pathology.';
      }
      if (lower.includes('brain') || lower.includes('cerebral')) {
        return 'Normal brain imaging study. No acute intracranial hemorrhage or territorial infarction.';
      }
      return 'Normal examination. No focal diagnostic abnormality identified.';
    }

    // Extract sentences with abnormal or key pathology markers
    const sentences = cleanText
      .split(/(?<=[.!?\n])\s+/)
      .map(s => s.trim())
      .filter(Boolean);

    const keySentences = sentences.filter(s => {
      const l = s.toLowerCase();
      return (
        l.includes('calculus') ||
        l.includes('stone') ||
        l.includes('hydronephrosis') ||
        l.includes('fracture') ||
        l.includes('infarct') ||
        l.includes('hemorrhage') ||
        l.includes('consolidation') ||
        l.includes('infiltrate') ||
        l.includes('nodule') ||
        l.includes('mass') ||
        l.includes('lesion') ||
        l.includes('effusion') ||
        l.includes('cyst') ||
        l.includes('lymphadenopathy') ||
        l.includes('thickening') ||
        l.includes('dilatation') ||
        l.includes('pneumothorax') ||
        l.includes('edema') ||
        l.includes('displacement')
      );
    });

    if (keySentences.length === 0) {
      return '';
    }

    // Combine top 1-3 findings cleanly into an impression statement
    const impressionText = keySentences.slice(0, 3).join(' ');
    return impressionText;
  }
}

const radiologyQualityChecker = new RadiologyQualityChecker();
export default radiologyQualityChecker;
